import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    Calculator,
    Loader2,
    RefreshCw,
    Scale,
    TrendingUp,
    Wallet,
} from "lucide-react";
import { degerKaybiMerkez, DK_ASAMALARI } from "../../lib/degerKaybiApi";

function fmt(n, opts = {}) {
    if (n == null || n === "") return "—";
    if (opts.percent) return `${Number(n).toLocaleString("tr-TR")}%`;
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

function KpiCard({ label, value, hint, accent, onClick, active }) {
    const rings = {
        rose: "border-rose-500/35 bg-gradient-to-br from-rose-500/10 to-[#0B0F19]/90",
        amber: "border-amber-500/35 bg-gradient-to-br from-amber-500/10 to-[#0B0F19]/90",
        gold: "border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/10 to-[#0B0F19]/90",
        violet: "border-violet-500/35 bg-gradient-to-br from-violet-500/10 to-[#0B0F19]/90",
        emerald: "border-emerald-500/35 bg-gradient-to-br from-emerald-500/10 to-[#0B0F19]/90",
        sky: "border-sky-500/35 bg-gradient-to-br from-sky-500/10 to-[#0B0F19]/90",
    };
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`rounded-xl border p-3.5 text-left transition-all ${rings[accent] || rings.violet} ${
                onClick ? "hover:scale-[1.01] cursor-pointer hover:border-[#D4AF37]/40" : ""
            } ${active ? "ring-2 ring-[#D4AF37]/45" : ""}`}
        >
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
            <div className="font-display text-xl md:text-2xl text-white mt-1 tabular-nums">{value}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </Tag>
    );
}

const KANBAN_STIL = {
    HESAPLANMADI: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    BASVURU: "border-cyan-500/35 bg-cyan-500/10 text-cyan-100",
    TAHKIM: "border-violet-500/35 bg-violet-500/10 text-violet-100",
    TAHSILAT: "border-emerald-500/35 bg-emerald-500/10 text-emerald-100",
};

export default function DegerKaybiMerkezPanel({
    filterParams = {},
    activeFilters = {},
    onFilterPatch,
    onRefresh,
    listCount,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await degerKaybiMerkez.ozet(filterParams));
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filterParams]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRefresh = () => {
        load();
        onRefresh?.();
    };

    const patchFilter = (patch) => {
        onFilterPatch?.(patch);
    };

    const kanban = data?.kanban || {};
    const activeAsama = activeFilters.dk_asama || "";

    return (
        <section className="mb-6 rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#121A2F] via-[#0f1424] to-[#1a1520] p-4 md:p-6 shadow-[0_0_48px_rgba(212,175,55,0.06)]">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] mb-2">
                        <Scale className="h-3.5 w-3.5" />
                        Değer Kaybı Merkezi
                    </div>
                    <h2 className="font-display text-xl md:text-2xl text-white tracking-tight">
                        KPI & süreç özeti
                        {listCount != null && (
                            <span className="ml-2 text-sm font-mono font-normal text-slate-400 border border-white/10 rounded-lg px-2 py-0.5">
                                listede {listCount}
                            </span>
                        )}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        Kartlara tıklayarak listeyi filtreleyin. Hesaplama motoru değişmez; yalnızca
                        görünürlük ve takip alanları genişletildi.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-sm text-[#F3E5AB] hover:bg-[#D4AF37]/20 disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        Yenile
                    </button>
                    <Link
                        to="/yeni/deger-kaybi-gelismis"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 text-sm text-slate-300 hover:bg-white/5"
                    >
                        <Calculator className="h-4 w-4 text-[#D4AF37]" />
                        Gelişmiş DK
                        <ArrowRight className="h-3 w-3 opacity-50" />
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5 mb-5">
                <KpiCard
                    label="Toplam dosya"
                    value={data?.toplam ?? "—"}
                    hint={
                        data?.hesaplanmamis != null
                            ? `${data.hesaplanmamis} hesaplanmamış`
                            : null
                    }
                    accent="gold"
                />
                <KpiCard
                    label="Hesaplanmamış"
                    value={data?.hesaplanmamis ?? "—"}
                    hint="Rayiç / parça / DK eksik"
                    accent="amber"
                    active={activeFilters.hesaplanmamis === "1"}
                    onClick={() =>
                        patchFilter({
                            hesaplanmamis: activeFilters.hesaplanmamis === "1" ? "" : "1",
                            dk_asama: "",
                        })
                    }
                />
                <KpiCard
                    label="Toplam talep (DK)"
                    value={fmt(data?.toplam_talep_dk)}
                    hint="Gerçek DK toplamı"
                    accent="violet"
                />
                <KpiCard
                    label="Tahkimde"
                    value={data?.tahkimde ?? "—"}
                    hint="Tahkim aşamasındaki dosyalar"
                    accent="rose"
                    active={activeAsama === "TAHKIM"}
                    onClick={() =>
                        patchFilter({
                            dk_asama: activeAsama === "TAHKIM" ? "" : "TAHKIM",
                            hesaplanmamis: "",
                        })
                    }
                />
                <KpiCard
                    label="Ort. DK / Rayiç"
                    value={
                        data
                            ? `${fmt(data.ortalama_dk)} / ${fmt(data.ortalama_rayic)}`
                            : "—"
                    }
                    accent="sky"
                />
                <KpiCard
                    label="Tahsilat oranı"
                    value={fmt(data?.tahsilat_orani, { percent: true })}
                    hint={
                        data?.toplam_tahsil_dk != null
                            ? `Tahsil: ${fmt(data.toplam_tahsil_dk)}`
                            : "Alınan ÷ Talep"
                    }
                    accent="emerald"
                    active={activeFilters.tahsil_edilmemis === "1"}
                    onClick={() =>
                        patchFilter({
                            tahsil_edilmemis:
                                activeFilters.tahsil_edilmemis === "1" ? "" : "1",
                            dk_asama: "",
                        })
                    }
                />
            </div>

            <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs uppercase tracking-[0.16em] text-slate-500">
                    Kanban — tıkla filtrele
                </span>
                {data?.bu_ay_yeni != null && (
                    <span className="ml-auto text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <Wallet className="h-3 w-3" />
                        Bu ay +{data.bu_ay_yeni} yeni DK
                    </span>
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {DK_ASAMALARI.map((a) => {
                    const count = kanban[a.value] ?? 0;
                    const active = activeAsama === a.value;
                    return (
                        <button
                            key={a.value}
                            type="button"
                            onClick={() =>
                                patchFilter({
                                    dk_asama: active ? "" : a.value,
                                    hesaplanmamis: "",
                                    tahsil_edilmemis: "",
                                })
                            }
                            className={`rounded-xl border px-3 py-3 text-left transition hover:scale-[1.01] ${
                                KANBAN_STIL[a.value]
                            } ${active ? "ring-2 ring-white/25" : "opacity-90 hover:opacity-100"}`}
                        >
                            <div className="text-[10px] uppercase tracking-wider opacity-80">
                                {a.label}
                            </div>
                            <div className="text-2xl font-display tabular-nums mt-0.5">{count}</div>
                        </button>
                    );
                })}
            </div>

            {data?.hesaplanmamis > 0 && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-100/90">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                    <div className="min-w-0 flex-1">
                        <p className="font-medium text-amber-50/95">
                            {data.hesaplanmamis} dosyada rayiç, parça veya DK hesabı eksik
                        </p>
                        {(data.hesaplanmamis_dosyalar || []).length > 0 ? (
                            <ul className="mt-2 space-y-1.5">
                                {data.hesaplanmamis_dosyalar.map((row) => (
                                    <li key={row.id} className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                                        <Link
                                            to={`/e/deger-kaybi/${row.id}`}
                                            className="font-semibold text-[#F3E5AB] hover:underline"
                                        >
                                            {row.dosya_no}
                                        </Link>
                                        <span className="text-slate-400">·</span>
                                        <span className="text-amber-50/90">{row.muvekkil_ad_soyad}</span>
                                        {row.uyari ? (
                                            <span className="text-amber-200/65">— {row.uyari}</span>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                        {data.hesaplanmamis_diger > 0 ? (
                            <p className="mt-1.5 text-amber-200/70">
                                … ve {data.hesaplanmamis_diger} dosya daha. Tümünü görmek için{" "}
                                <button
                                    type="button"
                                    className="underline text-[#F3E5AB] hover:text-white"
                                    onClick={() =>
                                        patchFilter({
                                            hesaplanmamis: "1",
                                            dk_asama: "",
                                        })
                                    }
                                >
                                    Hesaplanmamış
                                </button>{" "}
                                filtresine tıklayın.
                            </p>
                        ) : null}
                        <p className="mt-1.5 text-[11px] text-amber-200/55">
                            Evrak rozeti tabloda bekleyen belgeleri gösterir.
                        </p>
                    </div>
                </div>
            )}
        </section>
    );
}
